import { slugify } from "../../utils/formatters";

function toId(value) {
  return value == null ? "" : String(value);
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toOptionalNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function formatSize(sizeValue, sizeUnit) {
  if (sizeValue == null || sizeValue === "" || !sizeUnit) {
    return "";
  }

  return `${sizeValue}${sizeUnit}`;
}

function formatAttributeLabel(attributes) {
  return (Array.isArray(attributes) ? attributes : [])
    .map((attribute) => String(attribute?.attributeValue || "").trim())
    .filter(Boolean)
    .join(" ");
}

function formatVariantLabel(attributes, sizeValue, sizeUnit) {
  return [formatAttributeLabel(attributes), formatSize(sizeValue, sizeUnit)]
    .filter(Boolean)
    .join(" ")
    .trim();
}

export function normalizeCategory(item) {
  const id = toId(item?.id || item?.slug);
  const name = item?.name || "Unnamed category";

  return {
    id,
    name,
    slug: item?.slug || slugify(name) || id,
    code: item?.code || "",
    parentCategoryId:
      item?.parentCategoryId == null ? null : toId(item.parentCategoryId),
    description: item?.description || "",
    iconUrl: item?.iconUrl || "",
    imageUrl: item?.imageUrl || "",
    sortOrder: toNumber(item?.sortOrder),
    isActive: item?.isActive !== false,
    createdBy: toOptionalNumber(item?.createdBy) ?? 0,
    updatedBy: toOptionalNumber(item?.updatedBy),
    createdDate: item?.createdDate || null,
    updatedDate: item?.updatedDate || null,
    icon: item?.iconUrl || item?.imageUrl || "",
    thumbnail: item?.imageUrl || item?.iconUrl || "",
    updatedAt: item?.updatedDate || item?.createdDate || new Date().toISOString(),
  };
}

export function normalizeBrand(item) {
  const id = toId(item?.id || item?.code || item?.name);
  const name = item?.name || "Unnamed brand";

  return {
    id,
    name,
    code: item?.code || slugify(name).toUpperCase(),
    description: item?.description || "",
    logoUrl: item?.logoUrl || "",
    isActive: item?.isActive !== false,
    createdBy: toOptionalNumber(item?.createdBy) ?? 0,
    updatedBy: toOptionalNumber(item?.updatedBy),
    createdDate: item?.createdDate || null,
    updatedDate: item?.updatedDate || null,
    updatedAt: item?.updatedDate || item?.createdDate || new Date().toISOString(),
  };
}

export function normalizeProductRecord(item, categoriesMap = new Map(), brandsMap = new Map(), options = {}) {
  const product = item?.product || item || {};
  const variants = Array.isArray(item?.variants) ? item.variants : [];
  const images = Array.isArray(item?.images) ? item.images : [];
  const productCategory = product?.category || item?.category || null;
  const defaultVariant =
    options?.selectedVariant ||
    variants.find((variant) => variant?.isDefault) ||
    variants[0] ||
    null;

  const variantSku = defaultVariant?.sku || "";
  const matchedSkuImage = variantSku
    ? images.find((image) => image?.sku === variantSku)?.imageUrl
    : null;
  const primaryImage =
    matchedSkuImage ||
    images.find((image) => image?.isPrimary)?.imageUrl ||
    images[0]?.imageUrl ||
    "";

  const categoryId = toId(product?.categoryId ?? productCategory?.id);
  const brandId = toId(product?.brandId);
  const category = categoriesMap.get(categoryId);
  const brand = brandsMap.get(brandId);
  const price = toNumber(defaultVariant?.price);
  const originalPrice = toOptionalNumber(defaultVariant?.mrp ?? defaultVariant?.originalPrice);
  const stock = toNumber(defaultVariant?.quantityInStock);
  const inStock = stock > 0 && defaultVariant?.status !== "INACTIVE" && product?.isActive !== false;
  const unit = formatVariantLabel(
    defaultVariant?.attributes,
    defaultVariant?.sizeValue,
    defaultVariant?.sizeUnit
  ) || defaultVariant?.variantLabel || "";

  const displayName =
    defaultVariant?.displayName ||
    [product?.baseName, defaultVariant?.variantLabel].filter(Boolean).join(" ") ||
    product?.baseName ||
    "";

  const recordId =
    defaultVariant?.id
      ? toId(defaultVariant.id)
      : toId(product?.id) || slugify(product?.groupCode || product?.baseName || "product");

  return {
    id: recordId,
    productId: toId(product?.id),
    name: displayName,
    displayName,
    slug: defaultVariant?.productSlug || slugify(displayName || "product"),
    description: defaultVariant?.resolvedDescription || defaultVariant?.description || product?.sharedDescription || "",
    groupCode: product?.groupCode || "",
    baseName: product?.baseName || "",
    nameSuffix: product?.nameSuffix || "",
    sharedDescription: product?.sharedDescription || "",
    brandId,
    brandName: brand?.name || `Brand #${brandId || "N/A"}`,
    categoryId,
    categoryName:
      productCategory?.name || category?.name || `Category #${categoryId || "N/A"}`,
    isActive: product?.isActive !== false && defaultVariant?.status !== "INACTIVE",
    isFeatured: Boolean(defaultVariant?.isFeatured || product?.isFeatured),
    isBestSeller: Boolean(defaultVariant?.isBestSeller || product?.isBestSeller),
    createdBy: toOptionalNumber(product?.createdBy) ?? 0,
    updatedBy: toOptionalNumber(product?.updatedBy),
    rating: toNumber(product?.rating),
    reviewCount: toNumber(product?.reviewCount),
    image: primaryImage,
    gallery: images.map((image) => image?.imageUrl).filter(Boolean),
    images: images.map((image) => ({
      id: toId(image?.id),
      sku: image?.sku || defaultVariant?.sku || "",
      imageUrl: image?.imageUrl || "",
      isPrimary: Boolean(image?.isPrimary),
      sortOrder: toNumber(image?.sortOrder),
    })),
    sku: defaultVariant?.sku || "",
    unit,
    price,
    oldPrice:
      originalPrice && originalPrice > price ? originalPrice : undefined,
    sale: Boolean(originalPrice && originalPrice > price),
    discountPercentage:
      originalPrice && originalPrice > price
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : 0,
    stock,
    stockStatus: inStock ? "IN_STOCK" : "OUT_OF_STOCK",
    inStock,
    quickViewOnly: !inStock,
    tags: [productCategory?.name || category?.name, brand?.name].filter(Boolean),
    variant: defaultVariant
      ? {
          id: toId(defaultVariant?.id),
          label: defaultVariant?.variantLabel || unit,
          sizeValue:
            defaultVariant?.sizeValue == null ? "" : String(defaultVariant.sizeValue),
          sizeUnit: defaultVariant?.sizeUnit || "",
          price,
          originalPrice,
          stock,
          sku: defaultVariant?.sku || "",
          status: defaultVariant?.status || "ACTIVE",
          productSlug: defaultVariant?.productSlug || "",
          productName: defaultVariant?.productName || "",
          packType: defaultVariant?.packType || "",
          description: defaultVariant?.description || "",
          isFeatured: Boolean(defaultVariant?.isFeatured),
          isBestSeller: Boolean(defaultVariant?.isBestSeller),
          attributes: Array.isArray(defaultVariant?.attributes)
            ? defaultVariant.attributes.map((attribute) => ({
                attributeName: attribute?.attributeName || "",
                attributeValue: attribute?.attributeValue || "",
              }))
            : [],
        }
      : null,
    variants: variants.map((variant) => ({
      id: toId(variant?.id),
      productId: toId(variant?.productId),
      sku: variant?.sku || "",
      displayName: variant?.displayName || "",
      variantLabel: variant?.variantLabel || "",
      productSlug: variant?.productSlug || "",
      productName: variant?.productName || "",
      sizeValue: variant?.sizeValue == null ? "" : String(variant.sizeValue),
      sizeUnit: variant?.sizeUnit || "",
      packType: variant?.packType || "",
      description: variant?.description || "",
      price: toNumber(variant?.price),
      mrp: toOptionalNumber(variant?.mrp ?? variant?.originalPrice) ?? 0,
      quantityInStock: toNumber(variant?.quantityInStock),
      status: variant?.status || "ACTIVE",
      isFeatured: Boolean(variant?.isFeatured),
      isBestSeller: Boolean(variant?.isBestSeller),
      attributes: Array.isArray(variant?.attributes)
        ? variant.attributes.map((attribute) => ({
            attributeName: attribute?.attributeName || "",
            attributeValue: attribute?.attributeValue || "",
          }))
        : [],
    })),
    createdAt: product?.createdAt || null,
    updatedAt: product?.updatedAt || product?.createdAt || new Date().toISOString(),
  };
}

export function normalizeProductRecords(item, categoriesMap = new Map(), brandsMap = new Map()) {
  const variants = Array.isArray(item?.variants) ? item.variants : [];

  if (variants.length > 1) {
    return variants.map((variant) =>
      normalizeProductRecord(item, categoriesMap, brandsMap, { selectedVariant: variant })
    );
  }

  return [normalizeProductRecord(item, categoriesMap, brandsMap)];
}

export function normalizeProductPage(pagePayload, categoriesMap = new Map(), brandsMap = new Map()) {
  const rawItems = Array.isArray(pagePayload?.items) ? pagePayload.items : [];
  const items = rawItems.flatMap((item) => normalizeProductRecords(item, categoriesMap, brandsMap));

  return {
    items,
    page: toNumber(pagePayload?.page),
    size: toNumber(pagePayload?.size, 10),
    totalElements: items.length || toNumber(pagePayload?.totalElements),
    totalPages: toNumber(pagePayload?.totalPages),
    hasNext: Boolean(pagePayload?.hasNext),
    hasPrevious: Boolean(pagePayload?.hasPrevious),
  };
}

export function buildProductRequestPayload(draft, options = {}) {
  const {
    actorId = 0,
    mode = "create",
  } = options;

  const basePayload = {
    groupCode: String(draft?.groupCode || "").trim().toUpperCase(),
    baseName: String(draft?.baseName || draft?.name || "").trim(),
    nameSuffix: String(draft?.nameSuffix || "").trim(),
    sharedDescription: String(draft?.sharedDescription || draft?.description || "").trim(),
    brandId: Number(draft?.brandId) || 0,
    categoryId: Number(draft?.categoryId) || 0,
    isActive: Boolean(draft?.isActive),
    variants: (Array.isArray(draft?.variants) ? draft.variants : []).map((variant) => ({
      ...(variant.id ? { id: Number(variant.id) || 0 } : {}),
      sku: String(variant.sku || "").trim(),
      productSlug: slugify(variant.productSlug || variant.productName || draft?.baseName || ""),
      productName: String(variant.productName || "").trim(),
      sizeValue: Number(variant.sizeValue) || 0,
      sizeUnit: String(variant.sizeUnit || "").trim(),
      packType: String(variant.packType || "").trim(),
      description: String(variant.description || "").trim(),
      price: Number(variant.price) || 0,
      mrp: Number(variant.mrp) || 0,
      quantityInStock: Number(variant.quantityInStock) || 0,
      status: String(variant.status || "ACTIVE").trim().toUpperCase(),
      isFeatured: Boolean(variant.isFeatured),
      isBestSeller: Boolean(variant.isBestSeller),
      attributes: (Array.isArray(variant.attributes) ? variant.attributes : [])
        .map((attribute) => ({
          attributeName: String(attribute.attributeName || "").trim(),
          attributeValue: String(attribute.attributeValue || "").trim(),
        }))
        .filter((attribute) => attribute.attributeName && attribute.attributeValue),
    })),
    images: (Array.isArray(draft?.images) ? draft.images : []).map((image) => ({
      ...(image.id ? { id: Number(image.id) || 0 } : {}),
      sku: String(image.sku || draft?.variants?.[0]?.sku || "").trim(),
      imageUrl: String(image.imageUrl || "").trim(),
      sortOrder: Number(image.sortOrder) || 0,
      isPrimary: Boolean(image.isPrimary),
    })),
  };

  if (mode === "update") {
    return {
      ...basePayload,
      updatedBy: actorId,
    };
  }

  return {
    ...basePayload,
    createdBy: actorId,
  };
}
